import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { StreamStatus } from '@cookspace-shared-contracts/core';

import { useListStreamsQuery } from '@/api/streamsApi';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';

const STATUS_OPTIONS: Array<{ value: StreamStatus; label: string }> = [
    { value: 'scheduled', label: 'Запланирован' },
    { value: 'live', label: 'Идёт' },
    { value: 'ended', label: 'Завершён' },
    { value: 'cancelled', label: 'Отменён' }
];

const PREMIUM_OPTIONS: Array<{ value: 'true' | 'false'; label: string }> = [
    { value: 'true', label: 'Премиум' },
    { value: 'false', label: 'Обычные' }
];

const STATUS_LABEL: Record<StreamStatus, string> = {
    scheduled: 'Запланирован',
    live: 'Идёт',
    ended: 'Завершён',
    cancelled: 'Отменён'
};

const STATUS_COLOR: Record<StreamStatus, string> = {
    scheduled: 'bg-blue-100 text-blue-800',
    live: 'bg-red-100 text-red-800',
    ended: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-yellow-100 text-yellow-800'
};

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

export function StreamsPage(): ReactElement {
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<StreamStatus | ''>('');
    const [filterPremium, setFilterPremium] = useState<'' | 'true' | 'false'>('');

    const queryArg = useMemo(
        () => ({
            ...(submittedSearch ? { search: submittedSearch } : {}),
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(filterPremium === '' ? {} : { isPremium: filterPremium === 'true' }),
            limit: 50,
            offset: 0
        }),
        [submittedSearch, filterStatus, filterPremium]
    );

    const { data, isLoading, isError } = useListStreamsQuery(queryArg);

    const items = data?.items ?? [];

    return (
        <div data-testid="streams-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Стримы</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="streams-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                }}
                className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="streams-filters"
            >
                <div className="min-w-[240px] flex-1">
                    <Input
                        label="Поиск"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Название стрима"
                        data-testid="streams-search-input"
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as StreamStatus | '')}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="streams-filter-status"
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Премиум"
                        value={filterPremium}
                        onChange={(e) =>
                            setFilterPremium(e.target.value as '' | 'true' | 'false')
                        }
                        placeholder="Все"
                        options={PREMIUM_OPTIONS}
                        data-testid="streams-filter-premium"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="streams-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Найти
                </button>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="streams-error">
                    Не удалось загрузить стримы
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="streams-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_160px_120px_100px_120px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Название</span>
                        <span>Автор</span>
                        <span>Запланирован</span>
                        <span>Статус</span>
                        <span className="text-right">Зрители</span>
                        <span className="text-right">Регистрации</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Стримы не найдены
                        </div>
                    )}

                    {items.map((stream) => (
                        <div
                            key={stream.id}
                            className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_160px_120px_100px_120px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`stream-row-${stream.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {stream.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{stream.title}</span>
                            <span className="truncate text-gray-700">
                                {stream.authorDisplayName}
                            </span>
                            <span className="text-xs text-gray-600">
                                {formatDateTime(stream.scheduledAt)}
                            </span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[stream.status]}`}
                                    data-testid={`stream-status-${stream.id}`}
                                >
                                    {STATUS_LABEL[stream.status]}
                                </span>
                            </span>
                            <span className="text-right text-gray-700">
                                {stream.viewersCount}
                            </span>
                            <span className="text-right text-gray-700">
                                {stream.registrationsCount}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
